import { createStore } from "solid-js/store";
import { createAssetsManager, hydrateAssetsFromDb } from "./syncAssets";
import type { LocalSyncDb, SetSyncStore, SyncStoreState } from "./syncTypes";
import Dexie from "dexie";
import { isServer } from "solid-js/web";
import { createConceptsManager, hydrateConceptsFromDb } from "./syncConcepts";

export type SyncStore = ReturnType<typeof createSyncStore>;

const initLocalSyncDb = () => {
  if (isServer) return null;
  const db = new Dexie("localSync") as LocalSyncDb;
  db.version(1).stores({
    assetRecord: "id",
    conceptRecord: "id",
    syncMeta: "kind",
  });
  return db;
};

// Populates the store from the local db.
// Each hydration function handles its own slice of the store
const hydrateAll = async (db: LocalSyncDb | null, setState: SetSyncStore) => {
  if (!db) return;
  await Promise.all([hydrateAssetsFromDb(db, setState)]);
  await Promise.all([hydrateConceptsFromDb(db, setState)]);
  setState("initializing", false);
};

/**
 * Store for syncing data from Bluesky to a local IndexedDB database.
 * Enables real-time querying of entities for better UX, e.g. @mentioning files, projects, etc.
 */
export const createSyncStore = () => {
  const db = initLocalSyncDb();

  const [state, setState] = createStore<SyncStoreState>({
    /**
     * true on server and while the store is being hydrated from local db
     */
    initializing: true,
    asset: {
      records: {},
      meta: {
        kind: "assets",
        nextAfter: null,
        lastSeenId: null,
        lastSeenVersion: null,
      },
    },
    concept: {
      records: {},
      meta: {
        kind: "concepts",
        nextAfter: null,
        lastSeenId: null,
        lastSeenVersion: null,
      },
    },
  });
  hydrateAll(db, setState);

  const assets = createAssetsManager(db, state, setState);
  const concepts = createConceptsManager(db, state, setState);

  return {
    assets,
    concepts,
  };
};
